import { useEffect } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, encodeUriQuery } from '../services/api';
import { onAngularEvent } from '../services/angular-bridge';
import { useToastMutation } from './utils/mutation';
import { refreshBatchReviewMetrics } from './useReview';
import type { Resource, PageRedactions, ResourceNote, NamedEntitiesResponse, SimilarResourcesResponse, Translation } from '../types/resource';
import type { BulkSearch } from '../types/search';

const resourceKey = (projectId: string, collectionId: string, resourceId: string) =>
  ['resource', projectId, collectionId, resourceId] as const;

const redactionsKey = (projectId: string, collectionId: string, resourceId: string) =>
  ['resource-redactions', projectId, collectionId, resourceId] as const;

const notesKey = (projectId: string, collectionId: string, resourceId: string) =>
  ['resource-notes', projectId, collectionId, resourceId] as const;

const translationKey = (projectId: string, collectionId: string, resourceId: string) =>
  ['resource-translation', projectId, collectionId, resourceId] as const;

export function useResource(projectId: string, collectionId: string, resourceId: string, meta?: Record<string, unknown>) {
  return useQuery({
    queryKey: resourceKey(projectId, collectionId, resourceId),
    queryFn: () =>
      api.get<Resource>(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}`
      ),
    enabled: Boolean(projectId && collectionId && resourceId),
    meta,
  });
}

// Re-run processing on a resource (super-admin action). Fire-and-forget: the server
// queues the job and the resource updates via the normal feed; we just toast, matching
// Angular ResourceService.Reprocess.
export function useReprocess(projectId: string, collectionId: string, resourceId: string) {
  return useToastMutation(
    resourceKey(projectId, collectionId, resourceId),
    // POST with no body, matching Angular ResourceService.Reprocess.
    () => api.post(`/project/${projectId}/collection/${collectionId}/resource/${resourceId}/reprocess`),
    { success: () => ({ title: 'Reprocessing started', timeout: 2500 }) },
  );
}

export function useResourcePdf(
  projectId: string,
  collectionId: string,
  resourceId: string,
  directPath?: string,
  enabled = true
) {
  const path = directPath
    ? directPath
    : `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/pdf`;
  // Key by the source path (minus any pre-signed query string, which re-signs on every
  // fetch) so each source — main vs. endorsed — gets its own cache entry, and a replaced
  // PDF (new directPdfPath) maps to a fresh fetch instead of reusing the cached bytes.
  const sourceKey = path.split('?')[0];
  return useQuery({
    queryKey: ['resource-pdf', projectId, collectionId, resourceId, sourceKey],
    queryFn: () => api.getArrayBuffer(path),
    enabled: enabled && Boolean(projectId && collectionId && resourceId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useResourceText(
  projectId: string,
  collectionId: string,
  resourceId: string,
  rich = false,
  directPath?: string,
  enabled = true
) {
  const segment = rich ? 'richText' : 'text';
  return useQuery({
    queryKey: ['resource-text', projectId, collectionId, resourceId, segment, directPath ?? ''],
    queryFn: () => {
      const path = directPath
        ? directPath
        : `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/${segment}`;
      return api.getText(path);
    },
    enabled: enabled && Boolean(projectId && collectionId && resourceId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useResourceTranslation(
  projectId: string,
  collectionId: string,
  resourceId: string
) {
  return useQuery({
    queryKey: translationKey(projectId, collectionId, resourceId),
    queryFn: () =>
      api.get<Translation>(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/translation`
      ),
    enabled: Boolean(projectId && collectionId && resourceId),
  });
}

/** Saves reviewer edits to the translated text. */
export function useSaveTranslation(projectId: string, collectionId: string, resourceId: string) {
  return useToastMutation<unknown, string>(
    translationKey(projectId, collectionId, resourceId),
    (translatedText) =>
      api.post(`/project/${projectId}/collection/${collectionId}/resource/${resourceId}/translation`, {
        projectId,
        collectionId,
        resourceId,
        translatedText,
      }),
    {
      success: () => ({ title: 'Translated Text Successfully Saved.', timeout: 5000 }),
      error: () => ({ title: 'Translated Text Failed to Save.', timeout: 5000 }),
    }
  );
}

/**
 * Machine-translates the document, then refetches so the editor shows the result.
 * `sourceLanguage` is 'detect' or a language id.
 */
export function useAutoTranslate(projectId: string, collectionId: string, resourceId: string) {
  return useToastMutation<unknown, string>(
    translationKey(projectId, collectionId, resourceId),
    (sourceLanguage) =>
      api.put(`/project/${projectId}/collection/${collectionId}/resource/${resourceId}/translate`, {
        sourceLanguage,
      }),
    { success: () => ({ title: 'Auto-Translated Text Successfully Saved.', timeout: 5000 }) }
  );
}

export function useResourceRedactions(
  projectId: string,
  collectionId: string,
  resourceId: string
) {
  return useQuery({
    queryKey: redactionsKey(projectId, collectionId, resourceId),
    queryFn: () =>
      api.get<unknown>(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/redactions`
      ),
    // Handle both response shapes: bare array or { pageRedactions: [] } envelope
    select: (data): PageRedactions[] => {
      if (Array.isArray(data)) return data;
      if (data && typeof data === 'object' && 'pageRedactions' in data && Array.isArray((data as any).pageRedactions)) {
        return (data as any).pageRedactions;
      }
      return [];
    },
    staleTime: 0, // Redactions can be edited; always fetch fresh when viewer opens
    enabled: Boolean(projectId && collectionId && resourceId),
  });
}

export function useSaveRedactions(
  projectId: string,
  collectionId: string,
  resourceId: string
) {
  const qc = useQueryClient();
  const path = `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/redactions`;
  return useMutation({
    mutationFn: (pageRedactions: PageRedactions[]) =>
      pageRedactions.length > 0
        ? api.post(path, pageRedactions)
        : api.delete(path),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: redactionsKey(projectId, collectionId, resourceId),
      }),
  });
}

export function useDeleteRedactions(
  projectId: string,
  collectionId: string,
  resourceId: string
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.delete(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/redactions`
      ),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: redactionsKey(projectId, collectionId, resourceId),
      }),
  });
}

export function useResourceNotes(
  projectId: string,
  collectionId: string,
  resourceId: string
) {
  return useQuery({
    queryKey: notesKey(projectId, collectionId, resourceId),
    queryFn: () =>
      api.get<ResourceNote[]>(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/note`
      ),
    enabled: Boolean(projectId && collectionId && resourceId),
  });
}

export function useAddResourceTag(
  projectId: string,
  collectionId: string,
  resourceId: string
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; tagRelated: boolean }) =>
      api.post(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/tag`,
        body
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: resourceKey(projectId, collectionId, resourceId) });
      refreshBatchReviewMetrics(qc, projectId);
    },
  });
}

export function useRemoveResourceTag(
  projectId: string,
  collectionId: string,
  resourceId: string
) {
  const qc = useQueryClient();
  const key = resourceKey(projectId, collectionId, resourceId);
  return useMutation({
    mutationFn: ({ tagId, withRelated }: { tagId: string; withRelated: boolean }) =>
      api.delete(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/tag/${tagId}?withRelated=${withRelated}`
      ),
    onMutate: async ({ tagId }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Resource>(key);
      if (previous) {
        qc.setQueryData<Resource>(key, { ...previous, tags: previous.tags.filter((t) => t.id !== tagId) });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData<Resource>(key, ctx.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
      refreshBatchReviewMetrics(qc, projectId);
    },
  });
}

export function useCreateNote(
  projectId: string,
  collectionId: string,
  resourceId: string
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { note: string; noteCategoryName: string }) =>
      api.post(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/note`,
        body
      ),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: notesKey(projectId, collectionId, resourceId),
      }),
  });
}

export function useDeleteNote(
  projectId: string,
  collectionId: string,
  resourceId: string
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) =>
      api.delete(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/note/${noteId}`
      ),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: notesKey(projectId, collectionId, resourceId),
      }),
  });
}

export function useResourceChildren(
  projectId: string,
  collectionId: string,
  resourceId: string,
  page: number,
  limit = 20
) {
  return useQuery({
    queryKey: ['resource-children', projectId, collectionId, resourceId, page, limit],
    queryFn: () =>
      api.get<{ resources: Resource[]; totalResources: number }>(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/children?page=${page}&limit=${limit}`
      ),
    enabled: Boolean(projectId && collectionId && resourceId),
    staleTime: 60_000,
  });
}

export function useSearchTermHighlights(
  projectId: string,
  collectionId: string,
  resourceId: string,
  termId: string | null
) {
  return useQuery({
    queryKey: ['resource-term-highlights', projectId, collectionId, resourceId, termId],
    queryFn: () =>
      api.get<string[]>(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/terms/${termId}/highlights`
      ),
    enabled: Boolean(projectId && collectionId && resourceId && termId),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * "Search Hits" — snippets of this resource matching the search the user ran to find
 * it. Ports ResourceService.FindHighlights: a basic query goes to `/highlights` as a
 * `query` param with no body, an advanced expression to `/highlights_adv` as the body.
 *
 * Angular read the live query off `$rootScope.query` (set by SearchController); React
 * reads the same thing from searchStore. Like Angular, the search is not cleared when
 * the user leaves the results, so hits keep showing for later-opened resources.
 */
export function useSearchHits(
  projectId: string,
  collectionId: string,
  resourceId: string,
  search: BulkSearch | null
) {
  return useQuery({
    queryKey: [
      'resource-search-hits',
      projectId,
      collectionId,
      resourceId,
      search?.advanced ? JSON.stringify(search.expression) : search?.query ?? null,
    ],
    queryFn: () => {
      const base = `/project/${projectId}/collection/${collectionId}/resource/${resourceId}`;
      return search!.advanced
        ? api.post<string[]>(`${base}/highlights_adv`, search!.expression)
        // Same wire format as the search itself — a %3A-encoded field query returns no hits.
        : api.post<string[]>(`${base}/highlights?query=${encodeUriQuery(search!.query)}`);
    },
    enabled: Boolean(
      projectId &&
        collectionId &&
        resourceId &&
        search &&
        (search.advanced ? search.expression : search.query)
    ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useNamedEntityGroups(projectId: string, collectionId: string, resourceId: string) {
  return useQuery({
    queryKey: ['resource-named-entity-groups', projectId, collectionId, resourceId],
    queryFn: () =>
      api.get<NamedEntitiesResponse>(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/namedEntities?page=0&limit=20`
      ),
    enabled: Boolean(projectId && collectionId && resourceId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useNamedEntityItems(
  projectId: string,
  collectionId: string,
  resourceId: string,
  typeValue: string,
  enabled: boolean
) {
  return useInfiniteQuery({
    queryKey: ['resource-named-entity-items', projectId, collectionId, resourceId, typeValue],
    queryFn: ({ pageParam }) =>
      api.get<NamedEntitiesResponse>(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/namedEntities?groupColumn=type.value&groupKey=${typeValue}&page=${pageParam}&limit=20`
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage.totalExpandedGroupItemCount ?? 0;
      const loaded = allPages.reduce((n, p) => n + (p.expandedGroupItems?.length ?? 0), 0);
      return loaded < total ? allPages.length : undefined;
    },
    enabled: Boolean(projectId && collectionId && resourceId && typeValue && enabled),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSimilarResources(
  projectId: string,
  collectionId: string,
  resourceId: string,
  page: number
) {
  return useQuery({
    queryKey: ['resource-similar', projectId, collectionId, resourceId, page],
    queryFn: () =>
      api.get<SimilarResourcesResponse>(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/similar?page=${page}&limit=20`
      ),
    enabled: Boolean(projectId && collectionId && resourceId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useResourceThread(projectId: string, collectionId: string, resourceId: string) {
  return useQuery({
    queryKey: ['resource-thread', projectId, collectionId, resourceId],
    queryFn: () =>
      api.get<Resource[]>(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/thread`
      ),
    enabled: Boolean(projectId && collectionId && resourceId),
    staleTime: 5 * 60 * 1000,
  });
}

export interface PdfReplaceCompleteMessage {
  projectId?: string;
  collectionId?: string;
  resourceId?: string;
}

/**
 * Replacing a PDF is async: the upload returns as soon as the job is queued, and the new
 * document only exists once the backend emits `PdfReplaceComplete`. Angular has no handler
 * for this event — it was added for React.
 *
 * Invalidating the resource is what actually swaps the document: `directPdfPath` is part of
 * the resource, and `useResourcePdf` keys its cache by that path, so the refetched resource
 * pulls the new bytes on its own. The `resource-pdf` prefix is invalidated too, because a
 * replacement can reuse the same path (re-signed query string aside), which would otherwise
 * hit the cached buffer.
 *
 * Lives in the app-wide WebSocketBridge, not the viewer: the same event has to refresh the
 * viewer, the detached window, and a resource the user has since navigated away from and
 * may reopen from cache.
 */
/**
 * Refreshes the open document when its redaction job finishes.
 *
 * The rendered PDF is deliberately not invalidated: the viewer keeps showing the original
 * with redaction overlays on top. What changes is `directRedactedPdfPath` on the resource,
 * which is the file print and download hand over — without this, both keep serving the
 * unredacted PDF until the viewer is reopened.
 *
 * The frame carries a `downloadUrl` and is not reliably resource-scoped, so this is mounted
 * by the viewer and scoped to the document it has open, mirroring the Angular directive.
 * Ids are honoured when the frame does carry them.
 */
export function useRedactionEvents(projectId: string, collectionId: string, resourceId: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!projectId || !collectionId || !resourceId) return;
    return onAngularEvent('FeedService.RedactionEnd', (message: { resourceId?: string } | undefined) => {
      if (message?.resourceId && message.resourceId !== resourceId) return;
      queryClient.invalidateQueries({ queryKey: resourceKey(projectId, collectionId, resourceId) });
      queryClient.invalidateQueries({ queryKey: redactionsKey(projectId, collectionId, resourceId) });
    });
  }, [queryClient, projectId, collectionId, resourceId]);
}

export function useResourceEvents() {
  const queryClient = useQueryClient();

  useEffect(() =>
    onAngularEvent('FeedService.PdfReplaceComplete', (message: PdfReplaceCompleteMessage) => {
      const { projectId, collectionId, resourceId } = message ?? {};
      if (!projectId || !collectionId || !resourceId) return;

      queryClient.invalidateQueries({ queryKey: resourceKey(projectId, collectionId, resourceId) });
      queryClient.invalidateQueries({ queryKey: ['resource-pdf', projectId, collectionId, resourceId] });
    }),
  [queryClient]);
}
